"use client";

/**
 * steps renderer — mirrors v2 ProcessSteps (Renter/LenderSteps): eyebrow +
 * title + description, then a numbered horizontal step row with dotted
 * connectors. Visually similar to timeline but with a heading above (not
 * inside a bordered card) and a more open layout.
 *
 * Data:
 *   - section.content { eyebrow, title, subtitle }
 *   - section.cards [{ icon, title, description }]
 */

import type { VisualCmsSection } from "@/types/visual-cms";
import { SECTION_CONTAINER, sectionShellStyle } from "./section-shell";
import { SafeIcon } from "./SafeIcon";
import { CardDescriptionHtml } from "@/components/visual-cms/CardDescriptionHtml";

function readString(c: unknown, key: string): string {
  if (c && typeof c === "object" && !Array.isArray(c)) {
    const v = (c as Record<string, unknown>)[key];
    return typeof v === "string" ? v : "";
  }
  return "";
}

export function StepsSection({ section }: { section: VisualCmsSection }) {
  const shell = sectionShellStyle(section);
  const content = (section.content as Record<string, unknown>) ?? {};
  const eyebrow = readString(content, "eyebrow");
  const title = readString(content, "title");
  const subtitle = readString(content, "subtitle");
  const items = (section.cards ?? []).filter((c) => c.is_active !== false);

  return (
    <section className={shell.className} style={shell.style}>
      <div className={SECTION_CONTAINER}>
        {(eyebrow || title || subtitle) && (
          <div className="text-center max-w-2xl mx-auto mb-12">
            {/* {eyebrow && (
              <p className="font-mono text-[10px] font-medium uppercase tracking-[0.14em] text-green-500 mb-2">
                {eyebrow}
              </p>
            )} */}
            {title && (
              <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-gray-900 mb-3">
                {title}
              </h2>
            )}
            {subtitle && <p className="text-base text-gray-500">{subtitle}</p>}
          </div>
        )}

        <div className="relative grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-5 gap-y-10">
          {items.map((it, i) => (
            <div key={it.id} className="relative flex flex-col items-center text-center group">
              {i < items.length - 1 && (
                <div className="hidden lg:block absolute top-[82px] left-[calc(50%+44px)] right-[calc(-50%+44px)] z-0">
                  <svg width="100%" height="2" viewBox="0 0 100 2" preserveAspectRatio="none">
                    <line
                      x1="0"
                      y1="1"
                      x2="100"
                      y2="1"
                      stroke="#1E8C3A"
                      strokeWidth="1.5"
                      strokeDasharray="3 4"
                      strokeLinecap="round"
                    />
                  </svg>
                </div>
              )}

              <div className="relative z-10 w-8 h-8 rounded-full bg-gradient-to-br from-green-500 to-green-700 text-white flex items-center justify-center font-bold text-xs mb-3 shadow-[0_8px_18px_-6px_rgba(30,140,58,0.6)] ring-4 ring-white">
                {i + 1}
              </div>
              <div className="relative z-10 mb-4">
                <div className="absolute inset-0 rounded-full bg-green-200 blur-md opacity-50" />
                <div className="relative w-16 h-16 rounded-full bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center text-white shadow-[0_10px_24px_-8px_rgba(30,140,58,0.55)]">
                  <SafeIcon name={it.icon} className="h-6 w-6" />
                </div>
              </div>
              {it.title && (
                <h3 className="font-semibold text-base text-gray-900 mb-2">{it.title}</h3>
              )}
              {it.description && (
                <CardDescriptionHtml
                  as="p"
                  text={it.description}
                  className="text-[13.5px] text-gray-500 leading-relaxed max-w-[220px]"
                />
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
